import { ChangeDetectionStrategy, Component, computed, input, type Signal } from '@angular/core';
import { MatChip } from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';
import type { TableCellComponent } from '../table-cell/table-cell.component';
import type { ChipsCellParameters } from './chips-cell-parameters';
import type { ChipsCellData } from './chips-cell-data';

@Component({
  selector: 'pct-feature-table-material-chips-cell',
  template: ` <div class="flex gap-2">
    @for (chipData of visibleChips(); track chipData.id) {
      <mat-chip>{{ chipData.label }} </mat-chip>
    }
    @let tooltipValue = tooltip();
    @if (tooltipValue !== null) {
      <div class="self-end" [matTooltip]="tooltipValue">
        <mat-chip>...</mat-chip>
      </div>
    }
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    mat-chip.cursor-pointer ::ng-deep .mat-mdc-chip-action {
      cursor: pointer !important;
    }
  `,
  imports: [MatChip, MatTooltip],
})
export class ChipsCellComponent<
  T,
  FIELD_VALUE_TYPE,
  FIELD_VALUE_ID_KEY extends keyof FIELD_VALUE_TYPE,
  PARAMETERS extends ChipsCellParameters<FIELD_VALUE_TYPE, FIELD_VALUE_ID_KEY>,
> implements TableCellComponent<T, FIELD_VALUE_TYPE[], PARAMETERS> {
  readonly rowItem = input.required<T>();
  readonly value = input.required<FIELD_VALUE_TYPE[]>();
  readonly params = input.required<PARAMETERS>();

  protected readonly visibleChips: Signal<ChipsCellData<FIELD_VALUE_TYPE, FIELD_VALUE_ID_KEY>[]> =
    computed(() => {
      const params = this.params();
      const values = this.value();
      if (!Array.isArray(values)) {
        return [];
      }

      let totalLength = 0;
      const maxLength = params?.chipTextMaxLength ?? 40;
      const result: ChipsCellData<FIELD_VALUE_TYPE, FIELD_VALUE_ID_KEY>[] = [];
      for (const value of values) {
        const labelAndId = params.getLabelAndId(value);
        const label = labelAndId.label;
        if (totalLength + label.length <= maxLength) {
          result.push(labelAndId);
          totalLength += label.length;
        } else {
          return result;
        }
      }

      return result;
    });

  protected readonly tooltip = computed(() => {
    const params = this.params();
    const value = this.value();
    if (value !== null) {
      const values = value.map(category => params.getLabelAndId(category)).flat();
      if (values.length !== this.visibleChips().length) {
        return values.map(({ label }) => label).join(', ');
      }
    }
    return null;
  });
}
